import React, { useEffect, useState } from "react";
import ImageWithBasePath from "../../../core/img/imagewithbasebath";
import { Filter, Plus } from "feather-icons-react/build/IconComponents";
import Swal from "sweetalert2";
import { all_routes } from "../../../Router/all_routes";
import httpService from "../../../services/http/products/product.service";
import categoryHttpService from "../../../services/http/products/category.service";
import brandHttpService from "../../../services/http/products/brand.service";
import usePagination, {
  createInitialPagination,
} from "../../../custom-hooks/usePagination";
import { Link, useNavigate } from "react-router-dom";
import ConfirmationAlert from "../../../components/confirm-alert";
import UnlimitTable from "../../../components/ui-kit/unlimit-table";
import UnlimitBreadcrumb from "../../../components/ui-kit/unlimit-breadcrumb";

import { useTranslation } from "react-i18next";
import useRoleAndPerm from "../../../utils/permissionFunction";
import { useSelectedCountry } from "../../../feature-module/dashboard/hooks/useSelectedCountry";

const ProductList = () => {
  const { hasPermission } = useRoleAndPerm();
  const selectedCountry = useSelectedCountry();
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState([]);
  const [brand, setBrand] = useState([]);
  const [eanFilter, setEanFilter] = useState("");
  const [lmProductNoFilter, setLmProductNoFilter] = useState("");
  const [nameFilter, setNameFilter] = useState("");
  const [selectedCategoryId, setSelectedCategoryId] = useState(null);
  const [selectedBrandId, setSelectedBrandId] = useState(null);
  const { t } = useTranslation();
  const initialPagination = createInitialPagination(t);
  const { paginationParams, updatePaginationParams } =
    usePagination(initialPagination);
  const navigate = useNavigate();

  const getProductList = async (
    pagination = paginationParams.pagination,
    filters,
    sorter = {}
  ) => {
    const data = {
      page: pagination?.current ?? 1,
      per_page: pagination?.pageSize ?? 10,
      ...filters,
      ...sorter,
    };
    setLoading(true);
    await httpService
      .productList(data)
      .then((response) => {
        if (response.status == 200) {
          setProducts(response.data.data?.data);
        }
        updatePaginationParams({
          pagination: {
            ...paginationParams.pagination,
            current: response.data.data.current_page,
            total: response.data.data.total,
            pageSize: response.data.data.per_page,
          },
        });
      })
      .catch((error) => {
        console.error("Error:", error);
        updatePaginationParams({
          pagination: {
            ...paginationParams.pagination,
          },
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleTableChange = async (pagination, filters, sorter) => {
    filters = {
      ean: eanFilter,
      lm_product_number: lmProductNoFilter,
      name: nameFilter,
      category_id: selectedCategoryId?.value,
      brand_id: selectedBrandId?.value,
    };
    const nonNullFilters = Object.fromEntries(
      Object.entries(filters).filter(([, value]) => !!value)
    );
    if (sorter && sorter.field) {
      sorter = {
        sort_by: sorter.field,
        sort_direction: sorter.order == "ascend" ? "ASC" : "DESC",
      };
    }
    if (!pagination) {
      pagination = initialPagination;
    }
    await updatePaginationParams({
      pagination,
      sorter,
    });
    await getProductList(pagination, nonNullFilters, sorter);
  };

  useEffect(() => {
    fetchBrand();
    fetchCategory();
    handleTableChange();
  }, []);

  async function fetchCategory() {
    try {
      const Category = await categoryHttpService.categoriesList();
      setCategory(Category?.data?.data?.data);
    } catch (err) {
      console.log(err);
    }
  }

  const onClickExportAs = async (type) => {
    setExportLoading(true);
    try {
      const response = await httpService.exportProducts({ export_type: type });
      window.open(response.data.data, "_blank");
    } catch (error) {
      console.log("Error ", error);
    } finally {
      setExportLoading(false);
    }
  };

  async function fetchBrand() {
    try {
      const brand = await brandHttpService.brandsList();
      setBrand(brand?.data?.data?.data);
    } catch (err) {
      console.log(err);
    }
  }

  const onConfirmDelete = async (id) => {
    await httpService
      .deleteProduct(id)
      .then((response) => {
        if (response.status == 200) {
          Swal.fire({
            title: t("general.prompts.deleted"),
            text: t("product.productDeleteSuccess"),
            className: "btn btn-success",
            confirmButtonText: t("general.button.ok"),
            customClass: {
              confirmButton: "btn btn-success",
            },
          });
          getProductList();
        }
      })
      .catch((error) => {
        console.error("Error:", error);
      });
  };

  const addNewProduct = () => {
    navigate(all_routes.productCreate);
  };

  const columns = [
    {
      title: t("tableColumnName.srNo"),
      dataIndex: "index",
      align: "center",
      width: 80, // Fixed width for serial number
      render: (value, item, index) =>
        (paginationParams?.pagination?.current - 1) *
          paginationParams?.pagination?.pageSize +
        (index + 1),
    },
    {
      title: "EAN",
      dataIndex: "ean",
      sorter: true,
      width: 120, // Fixed width for EAN
      render: (text, record) => record?.ean || record?.ean,
    },
    {
      title: "LM Product No",
      dataIndex: "lm_product_number",
      sorter: true,
      width: 150, // Fixed width for LM Product Number
      render: (text, record) =>
        record?.lm_product_number || record?.lm_product_number,
    },
    {
      title: t("tableColumnName.productName"),
      dataIndex: "name",
      sorter: true,
      width: 200, // Fixed width for product name
      render: (text, record) => (
        <div className="d-flex align-items-center">
          <ImageWithBasePath
            type="link"
            src={
              record?.product_image ||
              record?.main_product_image ||
              "/assets/img/products/default-product.png"
            }
            alt="Product"
            style={{
              width: 30,
              height: 30,
              marginRight: 10,
              objectFit: "cover",
              flexShrink: 0, // Prevent image from shrinking
            }}
          />
          <span
            style={{
              wordBreak: "break-word",
              whiteSpace: "normal",
              lineHeight: "1.3",
              fontSize: "14px",
            }}
            title={record?.name} // Show full name on hover
          >
            {record?.name || record?.name}
          </span>
        </div>
      ),
    },
    {
      title: "UoM",
      dataIndex: "uom",
      sorter: false,
      width: 120, // Fixed width for UoM
      render: (text, record) =>
        record?.uom_code_1?.code
          ? `${record?.uom_code_1?.code} ${
              record?.uom_code_1_value
                ? "(" + record?.uom_code_1_value + ")"
                : ""
            }`
          : "-",
    },
    {
      title: "Zemic Weight (g)",
      dataIndex: "uom",
      sorter: false,
      width: 140, // Fixed width for weight
      render: (text, record) => record.actual_zemic_sensor_weight,
    },
    {
      title: t("tableColumnName.actions"),
      key: "actions",
      align: "center",
      width: 150, // Fixed width for actions
      fixed: "right", // Optional: Keep actions column always visible
      hidden: !hasPermission([
        "product-view",
        "product-edit",
        "product-delete",
      ]),
      render: (text, record) => (
        <div className="action-table-data">
          <div className="edit-delete-action">
            {hasPermission(["product-view"]) && (
              <a
                className="p-2"
                onClick={() => navigate(`/product/${record.id}/details`)}
                style={{ cursor: "pointer" }}
              >
                <i className="feather-eye"></i>
              </a>
            )}
            {hasPermission(["product-edit"]) && (
              <a
                className="p-2"
                onClick={() => navigate(`/product/${record.id}/edit`)}
                style={{ cursor: "pointer" }}
              >
                <i className="feather-edit-2 text-black"></i>
              </a>
            )}
            {hasPermission(["product-delete"]) && (
              <ConfirmationAlert onConfirm={() => onConfirmDelete(record.id)} />
            )}
          </div>
        </div>
      ),
    },
  ];

  const breadcrumbItems = [
    { label: "", link: "/", icon: "feather-home" },
    { label: t("product.productList"), active: true },
  ];

  const categoryOptions = [
    { value: null, label: t("inputField.placeholder.chooseCategory") },
    ...(category.map((category) => ({
      value: category?.id,
      label: category?.name,
    })) || []),
  ];

  return (
    <div>
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex flex-column">
              <div className="page-title">
                <h4>{t("product.productList")}</h4>
              </div>
              <UnlimitBreadcrumb items={breadcrumbItems} />
            </div>

            {hasPermission(["product-add"]) && (
              <div className="page-btn">
                <Link to="/product-create" className="btn btn-added">
                  <Plus className="me-2 iconsize" />
                  {t("general.button.addNew")}
                </Link>
              </div>
            )}
          </div>
          {/* /product list */}
          <div className="card table-list-card">
            <div className="card-body">
              <div className="table-top row">
                <div className="col-lg-3 col-sm-4 col-12">
                  <input
                    type="text"
                    placeholder="EAN No"
                    className="form-control form-control-sm formsearch-custom ps-2"
                    value={eanFilter}
                    onChange={(e) => setEanFilter(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        handleTableChange();
                      }
                    }}
                  />
                </div>
                <div className="col-lg-3 col-sm-4 col-12">
                  <input
                    type="text"
                    placeholder="LM Product No"
                    className="form-control form-control-sm formsearch-custom ps-2"
                    value={lmProductNoFilter}
                    onChange={(e) => setLmProductNoFilter(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        handleTableChange();
                      }
                    }}
                  />
                </div>
                <div className="col-lg-3 col-sm-4 col-12">
                  <input
                    type="text"
                    placeholder={t("tableColumnName.productName")}
                    className="form-control form-control-sm formsearch-custom ps-2"
                    value={nameFilter}
                    onChange={(e) => setNameFilter(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === "Enter") {
                        handleTableChange();
                      }
                    }}
                  />
                </div>
                <div className="col-lg-1 col-sm-4 col-12 ms-auto">
                  <div className="input-blocks mb-0">
                    <button
                      className="btn btn-filters col-auto ms-auto px-2"
                      style={{ width: "fit-content" }}
                      onClick={handleTableChange}
                    >
                      <i data-feather="search" className="feather-search m-0" />
                    </button>
                  </div>
                </div>
              </div>
              <div className="table-responsive">
                <UnlimitTable
                  pagination={paginationParams.pagination}
                  columns={columns}
                  dataSource={products}
                  rowKey={(record) => record.id}
                  loading={loading}
                  onChange={handleTableChange}
                />
              </div>
            </div>
          </div>
          {/* /product list */}
        </div>
      </div>
    </div>
  );
};

export default ProductList;
